import React, { createContext, useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import firebaseInstance from '../services/firebaseSingleton';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { setAuthToken, removeAuthToken, startPeriodicRefresh, stopPeriodicRefresh } from '../services/api';

const UserContext = createContext(null);

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authInitialized = useRef(false);
  const userRef = useRef(null); // Track current user to avoid stale closures
  const navigate = useNavigate();

  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      await firebaseInstance.signOut();
      // onAuthStateChanged will handle clearing user state
    } catch (err) {
      console.error("Sign out failed:", err);
      setError(err.message);
      // Force clear state even if signout fails
      setUser(null);
      setUserData(null);
      removeAuthToken();
      stopPeriodicRefresh();
      setLoading(false);
    } finally {
      navigate('/');
    }
  }, [navigate]);

  const signInWithCustomToken = useCallback(async (token) => {
    try {
      setLoading(true);
      const userCredential = await firebaseInstance.signInWithCustomToken(token);
      
      // Immediately set user state from the credential to avoid race condition
      // onAuthStateChanged will also fire, but this ensures immediate availability
      if (userCredential?.user) {
        const firebaseUser = userCredential.user;
        try {
          const idToken = await firebaseUser.getIdToken();
          setAuthToken(idToken);
          startPeriodicRefresh();
          
          const firestore = firebaseInstance.getFirestoreInstance();
          const userDocRef = doc(firestore, 'users', firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          setUser(firebaseUser);
          userRef.current = firebaseUser; // Update ref
          if (userDocSnap.exists()) {
            setUserData(userDocSnap.data());
          } else {
            console.warn(`No user document found for UID: ${firebaseUser.uid}. Waiting for creation...`);
            setUserData(null);
          }
          setLoading(false);
        } catch (authError) {
          console.error("Error processing authenticated user:", authError);
          setLoading(false);
          // Don't sign out here, let onAuthStateChanged handle it
        }
      }
      // onAuthStateChanged will also fire and may update state again, which is fine
    } catch (err) {
      console.error("Custom token sign in failed in context:", err);
      setError(err.message);
      setLoading(false);
      await signOut(); // Ensure clean state on failure
      throw err;
    }
  }, [signOut]);

  const refreshUserData = useCallback(async () => {
    if (!user) return;
    try {
      const firestore = firebaseInstance.getFirestoreInstance();
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        setUserData(userDocSnap.data());
      }
    } catch (err) {
      console.error("Failed to refresh user data:", err);
    }
  }, [user]);
  
  useEffect(() => {
    let unsubscribe;
    const initializeAuthListener = async () => {
      if (authInitialized.current) return;
      authInitialized.current = true;
      setLoading(true);

      try {
        if (!firebaseInstance.isInitialized()) {
          // Firebase config should be available from environment variables
          const firebaseConfig = {
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
            appId: import.meta.env.VITE_FIREBASE_APP_ID,
          };
          await firebaseInstance.initialize(firebaseConfig);
        }

        unsubscribe = firebaseInstance.onAuthStateChanged(async (firebaseUser) => {
          if (firebaseUser) {
            try {
              // Only update if user changed or if we don't have a user yet
              // This prevents unnecessary updates when signInWithCustomToken already set the user
              const currentUid = userRef.current?.uid;
              if (currentUid === firebaseUser.uid) {
                // Same user, just ensure loading is false
                setLoading(false);
                return;
              }
              
              const idToken = await firebaseUser.getIdToken();
              setAuthToken(idToken);
              startPeriodicRefresh();
              
              const firestore = firebaseInstance.getFirestoreInstance();
              const userDocRef = doc(firestore, 'users', firebaseUser.uid);
              const userDocSnap = await getDoc(userDocRef);

              setUser(firebaseUser);
              userRef.current = firebaseUser; // Update ref
              if (userDocSnap.exists()) {
                setUserData(userDocSnap.data());
              } else {
                console.warn(`No user document found for UID: ${firebaseUser.uid}. Waiting for creation...`);
                // It might take a moment for the user document to be created.
                // A more robust solution might involve retries or a listener.
                setUserData(null);
              }
            } catch (authError) {
              console.error("Error processing authenticated user:", authError);
              await signOut();
            } finally {
              setLoading(false);
            }
          } else {
            setUser(null);
            userRef.current = null; // Update ref
            setUserData(null);
            removeAuthToken();
            stopPeriodicRefresh();
            setLoading(false);
          }
        });
      } catch (err) {
        console.error("FATAL: Firebase initialization failed in UserContext:", err);
        setError(err.message);
        setLoading(false);
        authInitialized.current = false;
      }
    };

    initializeAuthListener();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [signOut]);

  const value = useMemo(() => ({
    user,
    userData,
    isAuthenticated: !!user,
    isGuest: userData?.isGuest || false, // Assuming guest status is in userData
    loading,
    error,
    signOut,
    refreshUserData,
    signInWithCustomToken,
  }), [user, userData, loading, error, signOut, refreshUserData, signInWithCustomToken]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};